/**
 * Normalisierung von IDs und Namen.
 *
 * Bewusst reine Funktionen ohne Abhängigkeiten: dieselbe Logik läuft beim
 * Import (Schreibseite) und bei jeder Prüfung (Abfrageseite). Als generierte
 * Datenbankspalte hätten wir zwei Implementierungen, und die driften.
 *
 * Diese Datei ist die Grundlage für die Entscheidung GRÜN vs. GELB in P5.
 * Jede Änderung hier verschiebt Trefferquoten. Entsprechend dicht getestet.
 */
/** Arabisch-indische Ziffern (U+0660–0669) und ihre erweiterte Form (U+06F0–06F9). */
const EASTERN_DIGITS = /[٠-٩۰-۹]/g;
function foldEasternDigits(input) {
    return input.replace(EASTERN_DIGITS, (ch) => {
        const code = ch.codePointAt(0);
        const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
        return String((code - base).toString());
    });
}
/**
 * Exakte Normalform einer ID-Nummer: nur [0-9A-Z], führende Nullen bleiben.
 *
 * Toleriert, was auf einer gescannten oder abgetippten Karte vorkommt:
 * Leerzeichen, Bindestriche, Punkte, Kleinschreibung, Präfixe wie "MKAD-".
 * Gibt `null` zurück, wenn nichts Verwertbares übrig bleibt.
 */
export function normalizeExtId(raw) {
    if (raw === null || raw === undefined)
        return null;
    const folded = foldEasternDigits(raw.normalize('NFKC'));
    const stripped = folded.toUpperCase().replace(/[^0-9A-Z]/g, '');
    return stripped.length > 0 ? stripped : null;
}
/**
 * Zweite Normalform ohne führende Nullen.
 *
 * "00123" → "123". Bewusst NICHT eindeutig: die beiden können zu
 * verschiedenen Personen gehören. Ein Treffer allein hierüber ist GELB.
 * "0000" → "0", damit eine reine Nullfolge nicht verschwindet.
 */
export function looseExtId(raw) {
    const norm = normalizeExtId(raw);
    if (norm === null)
        return null;
    return norm.replace(/^0+(?=.)/, '');
}
/**
 * Entfernt ein bekanntes Präfix vom Anfang einer normalisierten ID.
 *
 * Dienstkarten tragen die Nummer oft mit Vorsatz. "MKAD-00123" für die
 * Person "00123". Welcher Vorsatz das ist, wissen wir erst, wenn eine echte
 * Karte vorliegt (O-3), deshalb steht die Liste in der Konfiguration und
 * nicht im Code: ein überraschendes Kartenformat darf kein Redeploy
 * erzwingen.
 *
 * Es wird nur gestrippt, wenn danach noch etwas übrig bleibt.
 */
export function stripKnownPrefixes(norm, prefixes) {
    for (const prefix of prefixes) {
        const p = prefix.toUpperCase().replace(/[^0-9A-Z]/g, '');
        if (p && norm.startsWith(p) && norm.length > p.length)
            return norm.slice(p.length);
    }
    return norm;
}
/**
 * Normalisierter Namensschlüssel für die Ähnlichkeitssuche.
 *
 * Schritte: NFD-Zerlegung → kombinierende Zeichen entfernen (damit fallen
 * Diakritika wie ā, ṣ, ü und arabische Harakat weg) → Sonderbuchstaben
 * ersetzen → Trennzeichen zu Leerzeichen → Kleinschreibung → Leerraum
 * zusammenziehen.
 *
 * Arabische und Urdu-Schrift bleiben erhalten. Sie werden nicht
 * transliteriert, sondern als eigene Zeichen mitgesucht.
 *
 * Bekannte Grenze: "Müller" wird zu "muller", "Mueller" bleibt "mueller".
 * Diese beiden gleichzusetzen wäre gefährlich ("Neuer" → "Nur"). Die Lücke
 * schließt die Trigram-Ähnlichkeit in P5, nicht die Normalisierung.
 */
export function normalizeName(first, last) {
    return normalizeNameString(`${first ?? ''} ${last ?? ''}`);
}
/** Dieselbe Normalisierung für eine freie Sucheingabe. */
export function normalizeNameString(input) {
    if (!input)
        return '';
    return (input
        .normalize('NFD')
        // Kombinierende Zeichen: Akzente, Punkte, arabische Harakat.
        .replace(/\p{M}+/gu, '')
        // Sonderbuchstaben ohne Zerlegungsform.
        .replace(/ß/g, 'ss')
        .replace(/[øØ]/g, 'o')
        .replace(/[đĐ]/g, 'd')
        .replace(/[ł]/gi, 'l')
        .replace(/[æÆ]/g, 'ae')
        // Hamza, Ain und die diversen Apostroph-Varianten fallen ersatzlos weg:
        // "ʿAbd", "'Abd" und "Abd" sollen denselben Schlüssel ergeben.
        .replace(/[ʻʼʾʿ'’‘`´]/g, '')
        // Trennzeichen werden zu Leerraum: "Müller-Lüdenscheidt" ist zwei Wörter.
        .replace(/[-_./\\,]+/g, ' ')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim());
}
/**
 * Muster, mit denen die eigentliche Jamaat-ID aus einem Kartencode
 * herausgelöst wird.
 *
 * **Hintergrund (02.09.2026, O-3 beantwortet).** Die erste echte Dienstkarte
 * trägt den Code `2760004549401` für die Jamaat-ID `45494`. Der Code ist
 * dreiteilig: ein fester Vorsatz `276`, die auf acht Stellen mit Nullen
 * aufgefüllte Jamaat-ID (`00045494`) und eine zweistellige Kartennummer
 * (`01`). Vermutlich der Zähler für Ersatzkarten.
 *
 * Es ist **kein** gültiger EAN-13: die Prüfziffer wäre 7, nicht 1. Der Code
 * ist also frei vergeben, und die Aufteilung ist eine Festlegung derer, die
 * die Karten drucken. Nicht ein Standard, auf den wir uns berufen könnten.
 * Deshalb steht das Muster in der Konfiguration (`SCAN_ID_PATTERNS`) und
 * nicht im Code: eine zweite Kartenserie mit anderem Zuschnitt darf kein
 * Redeploy erzwingen.
 *
 * Der Fund aus dem Muster wird ausschließlich **exakt** gesucht, nie über
 * die lose Form. Ein falsch zugeschnittenes Muster führt damit zu ROT
 * („nicht auf der Liste"), niemals zu einer Freigabe der falschen Person.
 */
export const DEFAULT_SCAN_ID_PATTERNS = ['^276(\\d{8})\\d{2}$'];
/**
 * Wendet die Muster auf einen normalisierten Kartencode an und liefert die
 * gefundenen ID-Kandidaten. In der Reihenfolge der Muster.
 *
 * Ein Muster ohne Fangklammer liefert den ganzen Treffer. Ungültige Muster
 * werden übergangen statt zu werfen: eine kaputte Konfiguration darf den
 * Eingang nicht anhalten.
 */
export function extractScanIds(norm, patterns) {
    const found = [];
    for (const pattern of patterns) {
        let regex;
        try {
            regex = new RegExp(pattern);
        }
        catch {
            continue;
        }
        const match = regex.exec(norm);
        if (!match)
            continue;
        const raw = match[1] ?? match[0];
        const candidate = normalizeExtId(raw);
        // Führende Nullen fallen weg: die Karte füllt auf eine feste Breite auf,
        // in der Liste steht die Nummer ohne Auffüllung.
        const trimmed = candidate === null ? null : candidate.replace(/^0+(?=.)/, '');
        if (trimmed && !found.includes(trimmed))
            found.push(trimmed);
    }
    return found;
}
/**
 * Alle Schreibweisen, unter denen eine Eingabe gesucht wird. In der
 * Reihenfolge, in der gesucht werden soll.
 *
 * Die Reihenfolge ist die eigentliche Aussage: die Eingabe **unverändert**
 * zuerst. Eine Person, deren Nummer zufällig wie ein Kartencode aussieht,
 * darf nicht von der zerlegten Fassung verdrängt werden.
 *
 * Server und Gerät benutzen dieselbe Funktion. Zwei Fassungen laufen
 * auseinander, und dann ist dieselbe Karte offline GRÜN und online ROT.
 */
export function scanVariants(norm, prefixes, patterns) {
    const variants = [norm];
    const stripped = stripKnownPrefixes(norm, prefixes);
    if (!variants.includes(stripped))
        variants.push(stripped);
    for (const candidate of extractScanIds(norm, patterns)) {
        if (!variants.includes(candidate))
            variants.push(candidate);
    }
    return variants;
}
